/**
 * Onboarding API — typed client for the /welcome interview
 * (ONBOARDING-FLOW-SPEC.md; SMB-SELF-SERVE-COMPETITOR-PLAN.md Phase 1).
 *
 * Mirrors backend/src/api/routes/onboarding.py exactly (the shared
 * contract threads A/C build to):
 *
 *   GET  /api/onboarding/tree                 the versioned script as data
 *   GET  /api/onboarding/session               {exists, session|null}
 *   POST /api/onboarding/session               get-or-create, idempotent
 *   POST /api/onboarding/session/transitions    answer one step
 *
 * tenant_id is never sent from here — it is JWT context on the backend.
 */

import { createHeaders } from './apiClient';
import type { ApiErrorOptions, HandleResponseOptions } from './httpResponse';
import { ApiError, handleResponse } from './httpResponse';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// =============================================================================
// Wire types (mirrors src/api/routes/onboarding.py response models)
// =============================================================================

export type StepKind = 'chips' | 'text' | 'number' | 'budget' | 'list';

export interface TreeStep {
  id: string;
  prompt: string;
  kind: StepKind;
  options: string[];
  next: string | null;
  next_by_answer: Record<string, string>;
  required: boolean;
  max_items: number | null;
  /** spec #47 D6 — the floor this question is held to, in the agent's own
   * words. Optional: a step that carries none renders none, and the
   * renderer never invents one. */
  why?: string | null;
  /** spec #47 D6 — presentable labels for chip options whose canonical
   * value is not a phrase. Absent leaves `prettifyOption` in charge, so a
   * raw enum key still never reaches a human. */
  option_labels?: Record<string, string>;
}

export interface InterviewTree {
  first_step: string;
  terminal_step: string;
  steps: TreeStep[];
}

export interface TreeResponse {
  version: string;
  tree: InterviewTree;
  /** spec #47 P2 — whether THIS tenant's interview terminal renders the
   * operating plan for approval. A SERVER decision (the flag is evaluated
   * in the route), so a client cannot turn the new terminal on for
   * itself; absent or false keeps the shipped funnel exactly as it was. */
  plan_first_setup?: boolean;
}

export interface OnboardingSession {
  id: string;
  workspace_id: string | null;
  branch: string | null;
  tree_version: string;
  step_key: string;
  revision: number;
  answers: Record<string, unknown>;
  completed: boolean;
}

export interface SessionLookup {
  exists: boolean;
  session: OnboardingSession | null;
}

export interface BudgetEntry {
  scope: 'platform' | 'blended';
  platform?: string;
  amount: number;
  currency: string;
}

export interface TransitionPayload {
  revision: number;
  step_key: string;
  answer: unknown;
  transition_id: string;
}

export interface TransitionResult {
  session: OnboardingSession;
  /** True when a 409 stale_revision was recovered from — either by a
   * transparent retry at the corrected revision (our answer was still the
   * thing being asked for), or by adopting session state that had already
   * moved on without us. Callers use this to decide whether to surface a
   * "your session was updated" notice. */
  reconciled: boolean;
}

// =============================================================================
// Errors
// =============================================================================

/** Onboarding's named failure. `.status` / `.code` reads and
 * `instanceof` checks are unchanged; the constructor's third argument
 * moved from a positional `code` to the shared `{ code, detail }` bag. */
export class OnboardingApiError extends ApiError {
  constructor(message: string, status: number, options?: ApiErrorOptions) {
    super(message, status, options);
    this.name = 'OnboardingApiError';
  }
}

/** The 409 shape is `{code: "stale_revision", current_revision}` at the
 * TOP level (not FastAPI's `detail` envelope) — see onboarding.py. */
export class StaleRevisionError extends Error {
  currentRevision: number;

  constructor(currentRevision: number) {
    super('stale_revision');
    this.name = 'StaleRevisionError';
    this.currentRevision = currentRevision;
  }
}

const onboardingErrors: HandleResponseOptions = {
  ErrorClass: OnboardingApiError,
};

function workspaceQuery(workspaceId?: string | null): string {
  return workspaceId
    ? `?workspace_id=${encodeURIComponent(workspaceId)}`
    : '';
}

// =============================================================================
// API functions
// =============================================================================

export async function getTree(): Promise<TreeResponse> {
  const response = await fetch(`${API_BASE_URL}/api/onboarding/tree`, {
    headers: createHeaders(),
  });
  return handleResponse<TreeResponse>(response, onboardingErrors);
}

export async function getSession(
  workspaceId?: string | null
): Promise<SessionLookup> {
  const response = await fetch(
    `${API_BASE_URL}/api/onboarding/session${workspaceQuery(workspaceId)}`,
    { headers: createHeaders() }
  );
  return handleResponse<SessionLookup>(response, onboardingErrors);
}

/** Get-or-create on the (tenant, workspace) natural key — safe to call on
 * every /welcome mount; it naturally resumes an in-progress session. */
export async function createSession(
  workspaceId?: string | null
): Promise<{ session: OnboardingSession }> {
  const response = await fetch(`${API_BASE_URL}/api/onboarding/session`, {
    method: 'POST',
    headers: createHeaders(),
    body: JSON.stringify(workspaceId ? { workspace_id: workspaceId } : {}),
  });
  return handleResponse<{ session: OnboardingSession }>(response, onboardingErrors);
}

async function postTransition(
  payload: TransitionPayload,
  workspaceId?: string | null
): Promise<OnboardingSession> {
  const response = await fetch(
    `${API_BASE_URL}/api/onboarding/session/transitions${workspaceQuery(workspaceId)}`,
    {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify(payload),
    }
  );
  if (response.status === 409) {
    const body = await response.json().catch(() => ({}));
    throw new StaleRevisionError(body.current_revision);
  }
  const data = await handleResponse<{ session: OnboardingSession }>(
    response,
    onboardingErrors
  );
  return data.session;
}

/**
 * Answer one step, or replay a prior answer idempotently. On a 409
 * stale_revision, refetches the session and reconciles:
 *  - if the fresh session is still waiting on the same step, our answer is
 *    still the thing being asked for — retry once at the corrected
 *    revision (safe: transition_id is content-addressed on
 *    session/step/answer, so a repeat retry is still a no-op replay);
 *  - otherwise the session already moved on without us (another tab, a
 *    replayed OAuth callback) — adopt that state rather than replaying an
 *    answer that may no longer be reachable on the branch.
 */
export async function submitTransition(
  payload: TransitionPayload,
  workspaceId?: string | null
): Promise<TransitionResult> {
  try {
    const session = await postTransition(payload, workspaceId);
    return { session, reconciled: false };
  } catch (e) {
    if (!(e instanceof StaleRevisionError)) throw e;
    const lookup = await getSession(workspaceId);
    if (!lookup.session) throw e;
    if (lookup.session.step_key === payload.step_key) {
      const retried = await postTransition(
        { ...payload, revision: lookup.session.revision },
        workspaceId
      );
      return { session: retried, reconciled: true };
    }
    return { session: lookup.session, reconciled: true };
  }
}

/**
 * Where an authenticated visitor belongs: `/welcome` until onboarding is
 * complete, `/dashboards` after (T9). Shared by App.tsx's `/` route and
 * `/billing/success`'s post-checkout continuation, so the two never
 * disagree about what "done" means. Fails toward onboarding — the
 * resumable, idempotent front door — rather than a dashboard that may have
 * nothing provisioned yet.
 */
export async function resolveOnboardingDestination(): Promise<
  '/welcome' | '/dashboards'
> {
  try {
    const lookup = await getSession();
    if (!lookup.exists || !lookup.session?.completed) {
      return '/welcome';
    }
    return '/dashboards';
  } catch (err) {
    console.error('Failed to resolve onboarding destination:', err);
    return '/welcome';
  }
}
